import mapboxgl from 'mapbox-gl'
import * as turf from '@turf/turf'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { triggerPixelTransition } from './transition'
import { getLenis, stopLenis, startLenis } from './lenis'
import { playFinaleScene } from './finale'
import { playMusic } from '../components/music'

gsap.registerPlugin(ScrollTrigger)

// Coordinates
const RIJNSBURG: [number, number] = [4.4419, 52.1917] // Netherlands
const HOUSTON: [number, number] = [-95.3698, 29.7604] // Texas, USA

let map: mapboxgl.Map | null = null
let rijnsburgMarker: mapboxgl.Marker | null = null

export function initMap(): void {
  const mapContainer = document.getElementById('map')
  const btnYes = document.getElementById('btn-yes')
  const btnNo = document.getElementById('btn-no')

  if (!mapContainer) return

  // Get Mapbox token from environment variable
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

  if (!mapboxToken) {
    console.warn('Mapbox token not found. Add VITE_MAPBOX_TOKEN to your .env file.')
    // Show placeholder message
    mapContainer.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; text-align: center; padding: 2rem;">
        <p>Map will appear here once you add your Mapbox token to .env file</p>
      </div>
    `
    return
  }

  // Set access token
  mapboxgl.accessToken = mapboxToken

  // Initialize map showing both waypoints
  map = new mapboxgl.Map({
    container: mapContainer,
    style: 'mapbox://styles/mapbox/dark-v11',
    center: [-45, 42], // Atlantic - between both locations
    zoom: 1.8,
    pitch: 20,
    bearing: 0,
    // Disable all interactions
    interactive: false,
    dragPan: false,
    scrollZoom: false,
    doubleClickZoom: false,
    touchZoomRotate: false,
    keyboard: false,
  })

  map.on('load', () => {
    if (!map) return

    // Disable fog completely - let body background show through
    map.setFog({
      range: [0, 0],
      color: 'transparent',
      'high-color': 'transparent',
      'space-color': 'transparent',
      'horizon-blend': 0,
      'star-intensity': 0,
    })

    // Remove the background layer so body shows through
    if (map.getLayer('background')) {
      map.setPaintProperty('background', 'background-color', 'transparent')
    }

    // No scroll-based fog transition needed anymore - body background handles it
    
    // Make water a light complementary soft color
    if (map.getLayer('water')) {
      map.setPaintProperty('water', 'fill-color', '#D4C4E8')
    }
    
    // Make land a light pastel shade
    if (map.getLayer('land')) {
      map.setPaintProperty('land', 'background-color', '#F0E6F0')
    }

    // Hide all default labels
    const layers = map.getStyle().layers
    if (layers) {
      for (const layer of layers) {
        if (layer.type === 'symbol' && layer.id.includes('label')) {
          map.setLayoutProperty(layer.id, 'visibility', 'none')
        }
      }
    }

    // Add Rijnsburg marker (origin waypoint) with label
    const rijnsburgMarkerEl = document.createElement('div')
    rijnsburgMarkerEl.className = 'custom-marker-container'
    rijnsburgMarkerEl.innerHTML = `
      <div class="custom-marker"></div>
      <div class="custom-marker-label">Netherlands</div>
    `
    
    rijnsburgMarker = new mapboxgl.Marker(rijnsburgMarkerEl)
      .setLngLat(RIJNSBURG)
      .addTo(map)

    // Add Houston marker (destination waypoint) with label
    const houstonMarkerEl = document.createElement('div')
    houstonMarkerEl.className = 'custom-marker-container'
    houstonMarkerEl.innerHTML = `
      <div class="custom-marker"></div>
      <div class="custom-marker-label">Houston</div>
    `
    
    new mapboxgl.Marker(houstonMarkerEl)
      .setLngLat(HOUSTON)
      .addTo(map)

    // Add route source (initially empty)
    map.addSource('route', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    // Add route line layer (trail behind plane)
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      paint: {
        'line-color': '#C73E6D',
        'line-width': 3,
        'line-opacity': 0.6,
      },
    })

    // Add plane source (initially empty - plane hidden until animation starts)
    map.addSource('plane', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    })

    // Add plane icon layer
    map.addLayer({
      id: 'plane',
      type: 'symbol',
      source: 'plane',
      layout: {
        'icon-image': 'airport',
        'icon-size': 1.5,
        'icon-rotate': ['get', 'bearing'],
        'icon-rotation-alignment': 'map',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    })
  })

  // Button event listeners
  if (btnYes) {
    btnYes.addEventListener('click', handleYesClick)
  }

  if (btnNo) {
    btnNo.addEventListener('click', handleNoClick)
  }
}

async function handleYesClick(): Promise<void> {
  if (!map) return

  const btnYes = document.getElementById('btn-yes')
  const btnNo = document.getElementById('btn-no')
  const questionHeading = document.querySelector('.question__heading')
  const questionSection = document.getElementById('question')

  // Disable buttons
  if (btnYes) btnYes.setAttribute('disabled', 'true')
  if (btnNo) btnNo.setAttribute('disabled', 'true')

  // Smooth scroll to lock into the question section, then stop Lenis
  const lenis = getLenis()
  if (lenis && questionSection) {
    await new Promise<void>((resolve) => {
      lenis.scrollTo(questionSection, {
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        onComplete: () => {
          // Stop Lenis to lock scrolling during the animation
          stopLenis()
          resolve()
        }
      })
    })
  } else {
    // Fallback: just stop scrolling if Lenis isn't available
    stopLenis()
  }

  // Immediately update heading text with sequence
  if (questionHeading) {
    // First message: "I knew you'd say yes!"
    gsap.to(questionHeading, {
      opacity: 0,
      y: -20,
      duration: 0.3,
      onComplete: () => {
        questionHeading.textContent = "I knew you'd say yes teehee! 💕"
        gsap.to(questionHeading, {
          opacity: 1,
          y: 0,
          duration: 0.3,
        })
      },
    })

    // Second message: "I love you so much"
    setTimeout(() => {
      gsap.to(questionHeading, {
        opacity: 0,
        y: -20,
        duration: 0.3,
        onComplete: () => {
          questionHeading.textContent = "I love you so much 💗"
          gsap.to(questionHeading, {
            opacity: 1,
            y: 0,
            duration: 0.3,
          })
        },
      })
    }, 2500)

    // Third message: "You make me so happy"
    setTimeout(() => {
      gsap.to(questionHeading, {
        opacity: 0,
        y: -20,
        duration: 0.3,
        onComplete: () => {
          questionHeading.textContent = "You make me so happy 🥰"
          gsap.to(questionHeading, {
            opacity: 1,
            y: 0,
            duration: 0.3,
          })
        },
      })
    }, 5000)

    // Fourth message: "My beautiful bunny"
    setTimeout(() => {
      gsap.to(questionHeading, {
        opacity: 0,
        y: -20,
        duration: 0.3,
        onComplete: () => {
          questionHeading.textContent = "My beautiful bunny ❤️"
          gsap.to(questionHeading, {
            opacity: 1,
            y: 0,
            duration: 0.3,
          })
        },
      })
    }, 7500)
  }

  // Hide buttons immediately
  gsap.to([btnYes, btnNo], {
    opacity: 0,
    y: 20,
    duration: 0.3,
    stagger: 0.1,
  })

  // Animate route from Rijnsburg to Houston
  await animateRoute()

  // Start the music when the finale transition begins
  playMusic()

  // Trigger pixelated page transition
  // When screen is fully covered, instantly scroll to finale section
  await triggerPixelTransition(() => {
    // This runs while screen is covered with blocks
    const finaleSection = document.getElementById('finale')
    if (finaleSection) {
      // Make finale visible and instantly scroll to it (no smooth scroll - user won't see it)
      finaleSection.classList.add('is-visible')
      finaleSection.scrollIntoView({ behavior: 'instant' })
    }
  })

  // Animate finale content after transition reveals it
  showFinale()
}

function handleNoClick(): void {
  const btnNo = document.getElementById('btn-no')
  const questionSection = document.getElementById('question')
  if (!btnNo) return

  // Smooth scroll to lock into the question section (in case she's slightly off)
  const lenis = getLenis()
  if (lenis && questionSection) {
    lenis.scrollTo(questionSection, {
      duration: 0.5,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    })
  }

  // Fun animation - button runs away
  gsap.to(btnNo, {
    x: `+=${(Math.random() - 0.5) * 200}`,
    y: `+=${(Math.random() - 0.5) * 100}`,
    duration: 0.3,
    ease: 'power2.out',
  })
}

async function animateRoute(): Promise<void> {
  return new Promise((resolve) => {
    if (!map) {
      resolve()
      return
    }

    // Create a great circle route between the two points
    const route = turf.greatCircle(
      turf.point(RIJNSBURG),
      turf.point(HOUSTON),
      { npoints: 500 }
    )

    const routeCoordinates = (route.geometry as GeoJSON.LineString).coordinates as [number, number][]

    const planeSource = map.getSource('plane') as mapboxgl.GeoJSONSource
    const routeSource = map.getSource('route') as mapboxgl.GeoJSONSource
    
    const animationDuration = 10000 // 10 seconds total
    let startTime: number | null = null

    // Calculate initial bearing for plane
    const initialBearing = turf.bearing(
      turf.point(RIJNSBURG),
      turf.point(routeCoordinates[1])
    )

    // Step 1: Zoom into Rijnsburg waypoint
    map.flyTo({
      center: RIJNSBURG,
      zoom: 3,
      pitch: 30,
      bearing: 0,
      duration: 2000,
      essential: true,
    })

    // Use moveend event to properly sequence animations
    map.once('moveend', () => {
      if (!map || !planeSource) return
      
      // Step 2: HOLD - Transform waypoint to plane (same camera position)
      setTimeout(() => {
        if (rijnsburgMarker) {
          rijnsburgMarker.remove()
          rijnsburgMarker = null
        }

        // Show plane at Rijnsburg position
        planeSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {
                bearing: initialBearing,
              },
              geometry: {
                type: 'Point',
                coordinates: RIJNSBURG,
              },
            },
          ],
        })

        // Step 3: Start flight animation from same camera position
        setTimeout(() => {
          startTime = performance.now()
          requestAnimationFrame(animate)
        }, 500)
      }, 800) // Hold for 800ms before transforming
    })

    function animate(currentTime: number) {
      if (!map || !startTime) {
        resolve()
        return
      }

      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / animationDuration, 1)
      
      // Smooth easing
      const easedProgress = easeInOutCubic(progress)
      
      const index = Math.floor(easedProgress * (routeCoordinates.length - 1))
      const currentCoord = routeCoordinates[index]
      const nextIndex = Math.min(index + 1, routeCoordinates.length - 1)
      const nextCoord = routeCoordinates[nextIndex]

      // Calculate bearing for plane rotation
      const bearing = turf.bearing(
        turf.point(currentCoord),
        turf.point(nextCoord)
      )

      // Update plane position and rotation
      if (planeSource) {
        planeSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {
                bearing: bearing,
              },
              geometry: {
                type: 'Point',
                coordinates: currentCoord,
              },
            },
          ],
        })
      }

      // Update route trail (line from start to current position)
      if (routeSource) {
        routeSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: routeCoordinates.slice(0, index + 1),
              },
            },
          ],
        })
      }

      // Smoothly update camera to follow plane
      map.setCenter(currentCoord)

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        resolve()
      }
    }
  })
}

// Smooth easing function
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function showFinale(): void {
  playFinaleScene(() => {
    // Re-enable scrolling after finale intro is fully loaded
    startLenis()
  })
}

export function getMap(): mapboxgl.Map | null {
  return map
}
